// Browser-side failures the server never sees (e.g. "Failed to fetch") go to the global error log.
// Reports that cannot be sent are kept briefly in this browser and sent with the next report.
export type ClientErrorReport = { kind: string; method: string; route: string; status?: number | null; message: string; details?: string };

const STORAGE_KEY = "edubgt_pending_client_errors";
const MAX_PENDING = 20;

function readPending(storage: Pick<Storage, "getItem"> | null): Array<ClientErrorReport & { at: string }> {
  try {
    const parsed: unknown = JSON.parse(storage?.getItem(STORAGE_KEY) || "[]");
    return Array.isArray(parsed) ? parsed.slice(-MAX_PENDING) as Array<ClientErrorReport & { at: string }> : [];
  } catch { return []; }
}

function writePending(storage: Pick<Storage, "setItem" | "removeItem"> | null, items: Array<ClientErrorReport & { at: string }>) {
  try {
    if (!items.length) storage?.removeItem(STORAGE_KEY);
    else storage?.setItem(STORAGE_KEY, JSON.stringify(items.slice(-MAX_PENDING)));
  } catch { /* storage may be blocked */ }
}

// Only the path is reported: no query string, so no identifiers of other records leak into the log.
export function reportRoute(path: string) {
  return String(path || "").split("?")[0].slice(0, 300);
}

export function isNetworkFailure(error: unknown) {
  return error instanceof TypeError && /fetch|network|load failed|networkerror/i.test(error.message);
}

export function createClientErrorReporter(send: (report: ClientErrorReport & { at: string }) => Promise<unknown>, storage: Storage | null) {
  let sending = false;
  return async function report(entry: ClientErrorReport) {
    const pending = [...readPending(storage), { ...entry, route: reportRoute(entry.route), message: entry.message.slice(0, 500), at: new Date().toISOString() }];
    writePending(storage, pending);
    if (sending) return;
    sending = true;
    try {
      let queue = readPending(storage);
      while (queue.length) {
        await send(queue[0]);
        queue = readPending(storage).slice(1);
        writePending(storage, queue);
      }
    } catch { /* still offline: kept for the next report */ }
    finally { sending = false; }
  };
}

// The text a user sees instead of the browser's "Failed to fetch".
export const NETWORK_FAILURE_MESSAGE = "Lidhja me serverin u ndërpre para se të vinte përgjigjja. Kontrollo internetin dhe provo përsëri; puna jote nuk humbet. Gabimi u regjistrua për administratorin.";
